import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Shift a popup menu so it stays inside the viewport. The menu is anchored at
 * the click point (x, y); if its measured box would overflow the right or
 * bottom edge it's pulled back just enough to fit, never past the top/left
 * margin. Keeps a menu opened near a border (e.g. the graph view menu with the
 * wide "Replay From …" label) from being truncated.
 */
export function clampMenuToViewport(
  x: number,
  y: number,
  width: number,
  height: number,
  vw: number,
  vh: number,
  margin = 8,
): { left: number; top: number } {
  let left = x;
  let top = y;
  if (left + width > vw - margin) left = Math.max(margin, vw - width - margin);
  if (top + height > vh - margin) top = Math.max(margin, vh - height - margin);
  return { left, top };
}

export interface ContextMenuItem {
  label: string;
  /** Omitted for submenu parents (items with `children`), which only expand. */
  onSelect?: () => void;
  disabled?: boolean;
  /**
   * When defined, the item renders as a checkable toggle: a leading ✓ when
   * true, or a blank gutter of the same width when false (so labels stay
   * aligned). Omit entirely for plain (non-checkable) items.
   */
  checked?: boolean;
  /** When present, the item expands a submenu on hover instead of acting. */
  children?: ContextMenuItem[];
}

interface Props {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

// Acrylic Glass panel, matching .menu-dropdown in global.css. Menus are
// opaque (no blur/transparency) so they stay readable over busy HMI content.
const panelStyle: React.CSSProperties = {
  background: "var(--ui-menu-bg)",
  color: "var(--ui-text-primary)",
  border: "var(--ui-menu-border)",
  borderRadius: "var(--ui-card-radius)",
  boxShadow: "var(--ui-menu-shadow)",
  minWidth: 140,
  padding: 6,
  fontSize: 13,
  userSelect: "none",
};

/** One menu level. Renders its items and, on hover of a parent item, a nested
 *  panel flush to its right. Used for both the top level and submenus. */
function MenuPanel({
  items,
  onClose,
  style,
}: {
  items: ContextMenuItem[];
  onClose: () => void;
  style?: React.CSSProperties;
}) {
  const [openSub, setOpenSub] = useState<number | null>(null);
  // When any item in this panel is checkable, every item reserves the leading
  // ✓ gutter so checked and unchecked labels stay vertically aligned.
  const hasCheckable = items.some((item) => item.checked !== undefined);

  return (
    <div role="menu" style={{ ...panelStyle, ...style }}>
      {items.map((item, i) => {
        const hasChildren = !!item.children && item.children.length > 0;
        return (
          <div
            key={item.label}
            role="menuitem"
            aria-disabled={item.disabled || undefined}
            aria-haspopup={hasChildren || undefined}
            className={item.disabled ? "context-menu-item disabled" : "context-menu-item"}
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              padding: "6px 12px",
              cursor: item.disabled ? "default" : "pointer",
            }}
            onMouseEnter={() => {
              setOpenSub(hasChildren ? i : null);
            }}
            onClick={(e) => {
              if (hasChildren) {
                e.stopPropagation();
                return;
              }
              if (item.disabled) return;
              item.onSelect?.();
              onClose();
            }}
          >
            {hasCheckable && (
              <span
                aria-hidden="true"
                style={{ display: "inline-block", width: 16, textAlign: "center", flexShrink: 0 }}
              >
                {item.checked ? "✓" : ""}
              </span>
            )}
            <span style={{ flex: 1, whiteSpace: "nowrap" }}>{item.label}</span>
            {hasChildren && (
              <span aria-hidden="true" style={{ marginLeft: 12 }}>
                ▸
              </span>
            )}
            {hasChildren && openSub === i && (
              <MenuPanel
                items={item.children!}
                onClose={onClose}
                style={{ position: "absolute", left: "100%", top: -5 }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Floating, single-use context menu. Renders into document.body via a portal
 * so view-internal overflow:hidden clips don't cut it off. Supports nested
 * submenus (items with `children`). Closes on: item click, click-outside
 * (mousedown capture), Escape, scroll, blur.
 */
export function ContextMenu({ x, y, items, onClose }: Props) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  // Anchored at the click point, then clamped to the viewport once measured.
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: x, top: y });

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onScrollOrBlur = () => onClose();

    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScrollOrBlur, true);
    window.addEventListener("blur", onScrollOrBlur);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScrollOrBlur, true);
      window.removeEventListener("blur", onScrollOrBlur);
    };
  }, [onClose]);

  // Clamp into the viewport once the menu is mounted and measurable, so one
  // opened near the right/bottom edge isn't truncated. useLayoutEffect applies
  // the corrected position before paint, so there's no visible jump from the
  // click point. (In jsdom getBoundingClientRect is 0×0, so this is a no-op and
  // the menu stays at x/y.)
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setPos(clampMenuToViewport(x, y, width, height, window.innerWidth, window.innerHeight));
  }, [x, y, items]);

  return createPortal(
    <div ref={menuRef} style={{ position: "fixed", left: pos.left, top: pos.top, zIndex: 10000 }}>
      <MenuPanel items={items} onClose={onClose} />
    </div>,
    document.body,
  );
}
